<template>
  <div>
    <ReportMobileFrame
      v-if="$vuetify.breakpoint.smAndDown"
      :loading="isLoading"
      :empty="!isLoading && data.length === 0"
      empty-text="No timesheet data for the selected week."
    >
      <template #filters>
        <div class="report-dates-grid">
          <MobileDateField label="Week Start" :value="dates[0]" @input="onMobileDateChange(0, $event)" />
          <MobileDateField label="Week End" align="right" :value="dates[1] || dates[0]" @input="onMobileDateChange(1, $event)" />
        </div>
      </template>

      <div v-for="staff in data" :key="staff.staff.id">
        <ReportGroupHeader :label="staff.staff.name">
          <template #stats>
            <div class="stat">
              <span class="stat__label">Hours</span>
              <span class="stat__value">{{ staffHours(staff) }}</span>
            </div>
          </template>
        </ReportGroupHeader>
        <ReportListRow
          v-for="day in staff.days"
          :key="staff.staff.id + '-' + day.unix"
        >
          <template #leading>
            <div class="date-circle">{{ moment.unix(day.unix).format('DD') }}</div>
          </template>
          <template #title>{{ moment.unix(day.unix).format('dddd') }}</template>
          <template #subtitle>
            <span v-if="day.clockInTime">In {{ moment(day.clockInTime).format('HH:mm') }}</span>
            <span v-if="day.clockOutTime"> · Out {{ moment(day.clockOutTime).format('HH:mm') }}</span>
            <span v-if="day.clockInTime && !day.clockOutTime"> · Still clocked in</span>
            <span v-if="!day.clockInTime">Off</span>
          </template>
          <template #trailing v-if="day.minutes > 0">
            <span class="report-money">{{ day.hoursTotalFormatted }}</span>
          </template>
        </ReportListRow>
      </div>
    </ReportMobileFrame>

    <template v-else>
    <v-row justify="center">
      <v-toolbar flat class="toolbar-container" height='100'>
        <div class="toolbar-content">
          <div class="toolbar-datepicker">
            <DateRangePicker :initialDates="dates" @blur="updateDates"  />
          </div>

          <v-spacer></v-spacer>

          <v-btn class="search-btn" type="button" color="success" v-on:click="load">
            SEARCH
          </v-btn>
        </div>
      </v-toolbar>
    </v-row>
    <v-row>
      <div class="timesheet-table">
        <div class="timesheet-table-row">
          <div class="header"></div>
          <div class="header" v-for="date in dateHeaders" :key="date.idx">
            <div>{{ date.day }}</div>
            <div>{{ date.date }}</div>
          </div>
        </div>
        
        <div class="timesheet-table-row" v-for="staff in data" :key="staff.staff.id">
          <div class="staff">{{ staff.staff.name }}</div>
          <div class="header" v-for="day in staff.days" :key="day.unix">
            <div v-if="day.clockInTime != null" class="clock-timer">
              <v-icon>mdi-update</v-icon>
              <span>{{ moment(day.clockInTime).format("HH:mm") }}</span>
            </div>
            <div v-if="day.clockOutTime != null" class="clock-timer">
              <v-icon>mdi-timer</v-icon>
              <span>{{ moment(day.clockOutTime).format("HH:mm") }}</span>
            </div>
            <div v-if="day.clockInTime != null && day.clockOutTime == null" class="clock-timer">
              <v-icon>mdi-timer</v-icon>
              <span>N/A</span>
            </div>
            <div class="total-timer" v-if="day.minutes > 0">{{ day.hoursTotalFormatted }}</div>
          </div>
        </div>
      </div>
    </v-row>
    </template>
  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue';
import ReportMobileFrame from '@/components/reports/ReportMobileFrame.vue';
import ReportGroupHeader from '@/components/reports/ReportGroupHeader.vue';
import ReportListRow from '@/components/reports/ReportListRow.vue';
import MobileDateField from '@/components/reports/MobileDateField.vue';
import { reportService, productService } from '@/services';
import moment from 'moment';
import { userService } from '@/services';
import router from "../../router";

export default {

  name: 'Timesheets',
  components: {
    DateRangePicker,
    ReportMobileFrame,
    ReportGroupHeader,
    ReportListRow,
    MobileDateField
  },
  data: vm => ({
    dates: [moment().isoWeekday(1).startOf('isoweek').format('YYYY-MM-DD'), moment().isoWeekday(1).startOf('isoweek').add(7, 'd').format('YYYY-MM-DD')],

    dateHeaders: [],
    isLoading: false,
    data: [],
  }),
  created () {
    if (userService.isStaff()) {
      router.push('/products');
    }
    
    this.load();    
  },
  methods: {
    updateDates(newDates) {
      this.dates = newDates;
    },
    async load() {
      var startDate = this.dates[0];
      var endDate = this.dates[1];

      this.dateHeaders = [];
      for (var m = moment(startDate); m.diff(endDate, 'days') <= -1; m.add(1, 'days')) {
        this.dateHeaders.push({
          idx: m.weekday(),
          day: m.format('dddd'),
          date: m.format("DD/MM/YYYY"),
          sortBy: m.unix()
        });
      }

      this.dateHeaders = this.dateHeaders.sort((a, b) => a.sortBy - b.sortBy);

      this.isLoading = true;
      this.data = await reportService.getTimesheets(moment(startDate).format("YYYY-MM-DD"));
      for (let d of this.data) {
        d.days = d.days.sort((a, b) => a.unix - b.unix);
      }

      this.isLoading = false;
    },
    onMobileDateChange(index, value) {
      if (!value) return;
      const next = this.dates.slice();
      while (next.length <= index) next.push(value);
      next[index] = value;
      if (index === 0) next[1] = value;
      this.dates = next;
      this.load();
    },
    staffHours(staff) {
      const total = staff.days.reduce((s, d) => s + (d.minutes || 0), 0);
      const h = Math.floor(total / 60);
      const m = total % 60;
      return `${h}h ${m.toString().padStart(2, '0')}m`;
    }
  }
}
</script>

<style lang="scss" scoped>
 .toolbar-container {
    background: #FFF !important;
    padding: 10px;
    height: auto;
    border-radius: 8px;

    border: 1px solid lighten(#7B5CD8, 30);
  }

  .toolbar-content {
    display: flex;
    flex-direction: row;
    width: 100%;

    .toolbar-datepicker {
      width: 250px;
    }
  }

  .timesheet-table {
    margin-top: 20px;
    
    width: 100%;
    overflow-y: auto;

    .timesheet-table-row {
      display: flex;
      margin-bottom: 15px;
    }

    .header {
      flex: 1;
      text-align: center;
      padding: 10px;
      background: #FFF;
      min-width: 100px;
      border-right: 1px solid #CECECE;

      div {
        &:first-child {
          border-bottom: 1px solid blue;
          padding-bottom: 5px;
          margin-bottom: 5px;
        }
      }
    }
  }

  .staff {
    flex: 1;
    text-align: center;
    padding: 10px;
    min-width: 100px;

    background: #ededed;
    color: black;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-right: 1px solid #000;
  }

  .clock-timer {
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      padding-top: 2px;
      width: 50px;
    }
  }

  .total-timer {
    margin-top: 5px;
    font-weight: bold;
  }

  .date-circle {
    align-items: center;
    background: #e5e7eb;
    border-radius: 50%;
    color: var(--app-text);
    display: flex;
    font-size: 14px;
    font-weight: 700;
    height: 38px;
    justify-content: center;
    width: 38px;
  }
</style>
